import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";

const Preparation = () => {
  const images = [
    "/images/prep1.png",
    "/images/about5.jpeg",
    "/images/process6.jpeg",
    "/images/about1.jpeg",
  ];

  const steps = [
    {
      title: "Step 1: Premium Coconut Husk Selection & Harvesting",
      description: "Tirumala Coco Peat Industries leads in sustainable coconut husk processing in India. Our expert team carefully selects and harvests mature coconut husks from certified organic plantations. These husks undergo thorough cleaning and sorting to ensure maximum fiber quality, setting the foundation for our premium-grade coco peat products used in hydroponics and organic farming.",
      image: images[0],
      isImageLeft: true,
    },
    {
      title: "Step 2: Advanced Eco-Friendly Processing Technology",
      description: "Using state-of-the-art machinery, we implement our proprietary processing technique that preserves the natural properties of coconut husks. Our mechanical extraction process separates premium coir fibers from pith, maintaining optimal pH levels and EC balance. This eco-conscious approach ensures superior water retention and aeration capabilities in our final products, perfect for professional greenhouse cultivation.",
      image: images[2],
      isImageLeft: false,
    },
    {
      title: "Step 3: Industrial-Grade Compression & Export Packaging",
      description: "Tirumala's innovative compression technology transforms processed coco peat into various formats including 5kg blocks, grow bags, and customized solutions. Our unique packaging methods maintain product integrity during international shipping while reducing transport costs. Each product undergoes stringent quality checks to meet global agricultural standards and organic certification requirements.",
      image: images[1],
      isImageLeft: true,
    },
    {
      title: "Step 4: Quality Assurance & Global Distribution Network",
      description: "Our ISO-certified quality control laboratory conducts comprehensive testing for critical parameters including EC, pH, and beneficial microorganisms. Tirumala Coco Peat products are exported to over 20 countries, serving commercial greenhouses, nurseries, and organic farms. Our dedicated customer support ensures optimal product performance across different growing conditions and applications.",
      image: images[3],
      isImageLeft: false,
    },
  ];

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-out",
      once: true,
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>Tirumala Coco Peat Processing | Organic Coir Products Manufacturing</title>
        <meta 
          name="description" 
          content="Discover Tirumala Coco Peat's premium quality processing methods. Leading manufacturer of organic coco peat, grow bags & coir products for hydroponics and greenhouse cultivation." 
        />
        <meta 
          name="keywords" 
          content="Tirumala coco peat, organic coir processing, hydroponic growing media, greenhouse cultivation, coco peat manufacturer, grow bags India, sustainable agriculture, coir fiber processing" 
        />
        <meta property="og:title" content="Tirumala Coco Peat Processing | Premium Coir Products" />
        <meta property="og:description" content="Premium quality coco peat processing by Tirumala Industries. Certified organic, sustainable manufacturing for global agriculture." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://tirumalacocopeat.com/processing" />
      </Helmet>

      <div className="max-w-7xl mx-auto pt-16 py-12 px-6 space-y-12">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-extrabold text-gray-800">
            Tirumala Coco Peat Processing – Leading Organic Coir Manufacturer
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Experience premium quality coco peat products manufactured using advanced sustainable technologies. Trusted by commercial growers worldwide for superior crop yields and organic cultivation.
          </p>
        </div>

        {steps.map((step, index) => (
          <div
            key={index}
            data-aos="fade-right"
            className={`flex flex-col ${
              step.isImageLeft ? "lg:flex-row" : "lg:flex-row-reverse"
            } items-center gap-8`}
          >
            <div className="w-full lg:w-1/2 h-72">
              <img
                src={step.image}
                alt={`Tirumala Coco Peat ${step.title.toLowerCase()} process`}
                className="h-full w-full object-cover rounded-lg shadow-2xl border-4 border-green-600"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
            <div className="w-full lg:w-1/2 space-y-4">
              <h2 className="text-2xl font-semibold text-green-700">
                {step.title}
              </h2>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Why Choose Tirumala Coco Peat Products?
          </h3>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="p-6 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-2">Certified Organic</h4>
              <p className="text-gray-600">100% natural and certified organic products meeting international standards</p>
            </div>
            <div className="p-6 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-2">Quality Assured</h4>
              <p className="text-gray-600">Rigorous quality control and testing at every production stage</p>
            </div>
            <div className="p-6 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-2">Global Export</h4>
              <p className="text-gray-600">Trusted supplier to leading agricultural businesses worldwide</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Preparation;